import * as React from 'react';
import * as Sentry from '@sentry/browser';
import axios from 'axios';
import { FormQuestion } from '../types';
import { LabelButton, Icon, useToast } from '@teamsnap/snap-ui';
import api, { Response } from 'core/api';
import {
  LoadingType,
  usePostRegEditActions,
  usePostRegEditDrawer,
} from 'hooks/postRegEditDrawer';
import { SENTRY_TAGS_PRE } from 'core/error-utils';
import { AMPLITUDE_PRE_EDIT, useAmplitude } from 'hooks/amplitude';

export const fileValidations = {
  validateFileSize,
}

export function validateFileSize(file: File, maxMb = 40): string | null {
  if (file.size > maxMb * 1024 * 1024) {
    return `File size should be less than ${maxMb}MB`;
  }
  return null;
}

const openUrl = (url) => {
  window.open(url, '_blank');
};


export async function getViewableUrl<T>(
  formResultId: number,
  questionId: string | number
): Promise<Response<T>> {
  try {
    return await api.get<T>(
      `/registration_documents?form_field_id=${questionId}&form_result_id=${formResultId}`
    );
  } catch (e) {
    Sentry.captureException(e, {
      extra: { formResultId, questionId },
      tags: SENTRY_TAGS_PRE,
    });
    throw new Error('Failed to get document url');
  }
}
interface SignedUploadUrlResponse {
  url: string;
  contentType: string;
  fileName: string;
}
export async function uploadDocument<T>(
  formResultId: number,
  questionId: string | number,
  file: File
): Promise<T | string> {
  try {
    const signedUploadUrl = await api.get<SignedUploadUrlResponse>(
      `/registration_documents/get_upload_url?form_field_id=${questionId}&form_result_id=${formResultId}&file_extension=${file.name
        .split('.')
        .pop()
        .toLowerCase()}`
    );
    const uploadUrl = signedUploadUrl.data?.url as string;
    await axios.put(uploadUrl, file, {
      headers: {
        'Content-Type': signedUploadUrl.data?.contentType,
      },
    });
    return signedUploadUrl.data?.fileName;
  } catch (e) {
    Sentry.captureException(e, {
      extra: { formResultId, questionId },
      tags: SENTRY_TAGS_PRE,
    });
    throw new Error('Failed to upload document');
  }
}

export const FileInput = ({ question }: { question: FormQuestion }) => {
  const { currentFormState, dirtyFields, formResultId, loading } =
    usePostRegEditDrawer();
  const { updateFormValue, updateFormFieldFile, setLoading } = usePostRegEditActions();
  const { toast } = useToast();
  const { amplitudeTrack } = useAmplitude();
  const [viewableUrl, setViewableUrl] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const { value, file } = currentFormState[question.id][0] || {};
  const uploading = loading[LoadingType.UploadFile];

  const onUploadClick = () => {
    // upload UI is a button so focus on the hidden input
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const viewFile = async () => {
    // if we already generated the viewable url, open it
    if (viewableUrl) {
      openUrl(viewableUrl);
      return;
    }
    // if we have a new file, open it
    if (file) {
      const url = URL.createObjectURL(file);
      setViewableUrl(url);
      openUrl(url);
      return;
    }
    // if we have an existing file, open it
    if (value) {
      // view file
      try {
        const viewableUrlResp = await getViewableUrl(formResultId, question.id);
        // @ts-ignore
        const viewableUrl = viewableUrlResp?.data?.url as string;
        setViewableUrl(viewableUrl as string);
        openUrl(viewableUrl as string);
      } catch (e) {
        toast({
          title: e.message,
          variant: 'negative',
        });
      }
    }
  };

  const uploadFile = async (e) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const error = fileValidations.validateFileSize(e.target.files[0]);
    if (error) {
      toast({
        title: error,
        variant: 'negative',
        duration: 5000,
      });
      return;
    }
    const file = e.target.files[0];
    // upload file
    try {
      setLoading(LoadingType.UploadFile, true);
      const fileName = await uploadDocument(
        formResultId,
        question.id,
        file
      ) as unknown as string;
      updateFormValue(question.id, [fileName]);
      updateFormFieldFile(question.id, fileName, file);
      const url = URL.createObjectURL(file);
      setViewableUrl(url);
    } catch (e) {
      toast({
        title: e.message,
        variant: 'negative',
      });
    } finally {
      setLoading(LoadingType.UploadFile, false);
    }
  };

  const removeFile = () => {
    setViewableUrl(null);
    updateFormValue(question.id, []);
    updateFormFieldFile(question.id, '', null);
  };

  return (
    <div className="sui-border sui-border-solid sui-border-neutral-border sui-rounded sui-p-2 sui-mb-1" data-testid={`FileInput-${question.id}`}>
      <input
        type="file"
        ref={inputRef}
        className="sui-hidden"
        onChange={uploadFile}
        accept=".pdf,.jpg,.jpeg,.png"
      />
      <div className="sui-flex sui-justify-between sui-items-center sui-gap-1">
        <span className="sui-body-dense sui-flex sui-items-center sui-gap-1">
          {!!file && (
            <Icon
              name="check_circle"
              size="default"
              className="sui-text-positive-text"
            />
          )}
          <span data-testid="FileInput-label">
            {dirtyFields.includes(question.id)
            ? `${question.label} (Edited)`
            : question.label}
          </span>
        </span>
        {value ? (
          <span>
            <LabelButton
              data-testid="FileInput-view-file"
              labelText="View"
              sentiment="default"
              size="default"
              variantType="tertiary"
              onClick={() => {
                amplitudeTrack.preFormEdited(AMPLITUDE_PRE_EDIT.DOCUMENT_VIEWED);
                viewFile()
              }}
              disabled={uploading}
            />
            <LabelButton
              data-testid="FileInput-re-upload"
              labelText="Re-upload"
              sentiment="default"
              size="default"
              variantType="tertiary"
              onClick={() => {
                amplitudeTrack.preFormEdited(AMPLITUDE_PRE_EDIT.DOCUMENT_REPLACED);
                onUploadClick()
              }}
              loading={uploading}
            />
            <LabelButton
              data-testid="FileInput-delete-file"
              labelText="Delete"
              sentiment="negative"
              size="default"
              variantType="tertiary"
              onClick={() => {
                amplitudeTrack.preFormEdited(AMPLITUDE_PRE_EDIT.DOCUMENT_DELETED);
                removeFile()
              }}
              disabled={uploading}
            />
          </span>
        ) : (
          <span>
            <LabelButton
              data-testid="FileInput-upload-file"
              labelText="Upload"
              sentiment="default"
              size="default"
              variantType="tertiary"
              onClick={() => {
                amplitudeTrack.preFormEdited(AMPLITUDE_PRE_EDIT.DOCUMENT_UPLOADED);
                onUploadClick()
              }}
              loading={uploading}
            />
          </span>
        )}
      </div>
    </div>
  );
};
